import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { updateAdStep3, getMyAds } from '../../../services/p2pService';
import { useToast } from '../../../context/ToastContext';

const REGIONS = ['Kenya','Uganda','Tanzania','Rwanda','Nigeria','South Africa','Global'];
const KEY = 'p2p_step3';
function loadS() { try { return JSON.parse(sessionStorage.getItem(KEY) || '{}'); } catch { return {}; } }
function saveS(d: object) { sessionStorage.setItem(KEY, JSON.stringify(d)); }

export default function PostAdStep3() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const saved = loadS();
  const [terms, setTerms] = useState(saved.terms || '');
  const [autoReply, setAutoReply] = useState(saved.autoReply || '');
  const [minDays, setMinDays] = useState(saved.minDays || '');
  const [regions, setRegions] = useState<string[]>(saved.regions || []);
  const [loading, setLoading] = useState(false);
  const [prefilled, setPrefilled] = useState(false);

  // Pre-fill terms and auto-reply from most recent published ad (only if fields are empty)
  useEffect(() => {
    if (prefilled) return;
    if (saved.terms || saved.autoReply) { setPrefilled(true); return; } // already have saved values
    const currentDraftId = sessionStorage.getItem('p2p_draft_id');
    getMyAds().then(ads => {
      // Find most recent non-draft ad that is not the current draft
      const prev = ads
        .filter((a: any) => a.status !== 'draft' && a._id !== currentDraftId)
        .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
      if (prev) {
        if (!terms && prev.terms) setTerms(prev.terms);
        if (!autoReply && prev.autoReply) setAutoReply(prev.autoReply);
        if (!minDays && prev.counterpartyConditions?.registeredDaysAgo)
          setMinDays(String(prev.counterpartyConditions.registeredDaysAgo));
      }
      setPrefilled(true);
    }).catch(() => setPrefilled(true));
  }, []);

  // Persist on every change
  useEffect(() => {
    saveS({ terms, autoReply, minDays, regions });
  }, [terms, autoReply, minDays, regions]);

  const toggleRegion = (r:string) => setRegions(prev=>prev.includes(r)?prev.filter(x=>x!==r):[...prev,r]);

  const next = async () => {
    const draftId = sessionStorage.getItem('p2p_draft_id');
    if (!draftId) { toast('Draft not found. Start over.','error'); navigate('/p2p/post-ad/1'); return; }
    setLoading(true);
    try {
      await updateAdStep3(draftId,{ terms, autoReply, counterpartyConditions:{ registeredDaysAgo:minDays?parseInt(minDays):undefined }, displayRegions:regions });
      navigate('/p2p/post-ad/preview');
    } catch(e:any){ toast(e.message,'error'); }
    setLoading(false);
  };

  return (
    <div style={{ background:'var(--p2p-bg-primary)', height:'100dvh', display:'flex', flexDirection:'column', overflow:'hidden' }}>
      <div className="p2p-header" style={{ flexShrink:0 }}>
        <button className="p2p-back-btn" onClick={()=>navigate(-1)}><ArrowLeft size={20}/>Post Ad</button>
      </div>
      <div style={{ flex:1, overflowY:'auto', padding:16, display:'flex', flexDirection:'column', gap:20 }}>
        <div className="p2p-steps">
          <div className="p2p-step-dot done">✓</div>
          <div className="p2p-step-line done"/>
          <div className="p2p-step-dot done">✓</div>
          <div className="p2p-step-line done"/>
          <div className="p2p-step-dot active">3</div>
        </div>

        <div>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
            <span style={{ fontSize:13, color:'var(--p2p-text-muted)' }}>Trade Terms</span>
            {terms && prefilled && saved.terms !== terms && <span style={{ fontSize:11, color:'var(--p2p-buy)', background:'var(--p2p-buy-subtle)', padding:'2px 8px', borderRadius:4 }}>pre-filled from last ad</span>}
          </div>
          <textarea className="p2p-input" style={{ minHeight:100, resize:'vertical' }} placeholder="Enter your terms..." value={terms} onChange={e=>setTerms(e.target.value)} />
        </div>

        <div>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
            <span style={{ fontSize:13, color:'var(--p2p-text-muted)' }}>Auto-Reply Message</span>
            {autoReply && prefilled && saved.autoReply !== autoReply && <span style={{ fontSize:11, color:'var(--p2p-buy)', background:'var(--p2p-buy-subtle)', padding:'2px 8px', borderRadius:4 }}>pre-filled from last ad</span>}
          </div>
          <textarea className="p2p-input" style={{ minHeight:80, resize:'vertical' }} placeholder="Sent automatically when order is placed" value={autoReply} onChange={e=>setAutoReply(e.target.value)} />
        </div>

        <div>
          <div style={{ fontSize:13, color:'var(--p2p-text-muted)', marginBottom:8 }}>Counterparty Conditions</div>
          <div style={{ fontSize:13, color:'var(--p2p-text-secondary)', marginBottom:6 }}>Min days registered</div>
          <input className="p2p-input" placeholder="e.g. 30" value={minDays} onChange={e=>setMinDays(e.target.value.replace(/[^0-9]/g,''))} />
        </div>

        <div>
          <div style={{ fontSize:13, color:'var(--p2p-text-muted)', marginBottom:8 }}>Display Regions</div>
          <div className="p2p-chip-row">{REGIONS.map(r=><button key={r} className={`p2p-chip ${regions.includes(r)?'selected':''}`} onClick={()=>toggleRegion(r)}>{r}</button>)}</div>
        </div>

      </div>
      <div style={{ flexShrink:0, padding:'12px 16px calc(12px + env(safe-area-inset-bottom)) 16px', marginBottom:'calc(64px + env(safe-area-inset-bottom))', borderTop:'1px solid var(--p2p-border-default)', background:'var(--p2p-bg-primary)' }}>
        <button
          className="p2p-btn-yellow"
          disabled={loading || terms.trim().length === 0}
          onClick={next}
        >
          {loading ? 'Saving...' : 'Preview'}
        </button>
      </div>
    </div>
  );
}
